import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, Mail, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

function formatError(detail) {
  if (!detail) return "Une erreur s'est produite";
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const AdminLogin = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const data = await login(email, password);
      if (data.role !== 'admin') {
        setError('Accès réservé aux administrateurs');
        return;
      }
      toast.success('Connexion réussie');
      navigate('/admin');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-6">
          <img
            src="https://customer-assets.emergentagent.com/job_fbe0f837-d33d-4f84-bae0-21bace0d1680/artifacts/n9bb0eio_NEOPLIAGE%20sans%20fond.png"
            alt="NEOPLIAGE"
            className="h-24"
          />
        </Link>
        <div className="bg-white rounded-lg shadow-lg p-8 border border-gray-100">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Espace administrateur</h1>
          <p className="text-gray-600 mb-8 text-sm">
            Connectez-vous pour accéder au tableau de bord
          </p>

          {error && (
            <div
              data-testid="admin-login-error"
              className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start gap-2"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="admin-login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#E42313]"
                  placeholder="admin@neopliage.fr"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="admin-login-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#E42313]"
                />
              </div>
            </div>
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={submitting}
              className="w-full bg-[#E42313] text-white py-3 rounded font-semibold hover:bg-[#C11A0F] transition-colors disabled:opacity-50"
            >
              {submitting ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>

          <p className="text-center text-xs text-gray-400 mt-6">
            Accès réservé au personnel NEOPLIAGE
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
